// Cave84 — Producers hooks (Phase 1 Week 3)
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Producer } from '@cave84/shared-types';
import {
  createProducer, getProducer, listProducers, updateProducer,
  type ProducerCreateInput, type ProducerUpdateInput
} from '@/services/producers';
import { qk } from '@/services/firestore';
import { useAuthStore } from '@/stores/auth.store';
import { foldSearch } from '@/utils/searchText';

function assertOwner() {
  const role = useAuthStore.getState().userDoc?.role;
  if (role !== 'owner') throw new Error('Cave84: owner role required.');
}

/** All producers — cached. Filtering is done client-side. */
export function useProducers() {
  return useQuery({ queryKey: qk.producers.all, queryFn: listProducers });
}

export function useProducer(id: string | undefined) {
  return useQuery({
    queryKey: id ? qk.producers.byId(id) : qk.producers.byId('__noop__'),
    queryFn: () => (id ? getProducer(id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

/** Client-side search over the cached producers list. */
export function useProducerSearch(term: string) {
  const { data: producers } = useProducers();
  const lower = foldSearch(term.trim());
  if (!lower || !producers) return producers ?? [];
  return producers.filter(p =>
    foldSearch(p.name).includes(lower) ||
    p.aliases.some(a => foldSearch(a).includes(lower))
  );
}

export function useCreateProducer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProducerCreateInput) => { assertOwner(); return createProducer(input); },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.producers.all }); },
  });
}

export function useUpdateProducer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ProducerUpdateInput }) => {
      assertOwner(); return updateProducer(id, patch);
    },
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: qk.producers.all });
      void qc.invalidateQueries({ queryKey: qk.producers.byId(id) });
    },
  });
}

/** Convenience: build a Map<id, Producer> from the cached list. */
export function useProducersMap(): Map<string, Producer> {
  const { data } = useProducers();
  return new Map((data ?? []).map(p => [p.id, p]));
}
